// frontend/src/modules/email/pages/DeliverabilityTab.tsx
//
// Sender-domain verification (SPF/DKIM) + suppressed-recipients management.

import * as React from 'react';
import { toast } from 'sonner';
import { Copy, Globe, MailX, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { emailApi } from '../api';
import type { EmailSuppression, SenderDomainState, SenderDomainStatus } from '../types';

export function DeliverabilityTab() {
  return (
    <div className="max-w-4xl space-y-6">
      <SenderDomainSection />
      <SuppressionsSection />
    </div>
  );
}

// ─── Sender domain ────────────────────────────────────────────────────────────

function SenderDomainSection() {
  const [state, setState] = React.useState<SenderDomainState | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [domainInput, setDomainInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    emailApi.getSenderDomain()
      .then(setState)
      .catch((err: any) => toast.error('Could not load sender domain', { description: err.message }))
      .finally(() => setLoading(false));
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!domainInput.trim()) return;
    setBusy(true);
    try {
      setState(await emailApi.createSenderDomain(domainInput.trim()));
      setDomainInput('');
      toast.success('Domain added — now publish the DNS records below');
    } catch (err: any) {
      toast.error('Could not add domain', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    setBusy(true);
    try {
      const next = await emailApi.verifySenderDomain();
      setState(next);
      if (next.status === 'verified') toast.success('Domain verified — emails now send from your domain');
      else if (next.status === 'failed') toast.error('Verification failed — double-check the DNS records');
      else toast.info('Still pending — DNS changes can take up to a few hours to propagate');
    } catch (err: any) {
      toast.error('Verify failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await emailApi.deleteSenderDomain();
      setState({ domain: null, status: 'unconfigured', records: [] });
      toast.success('Sender domain removed');
    } catch (err: any) {
      toast.error('Remove failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  function copy(value: string) {
    navigator.clipboard.writeText(value).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed'),
    );
  }

  return (
    <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={VISUAL.dashSection}>Sender domain</h3>
        {state && state.domain && domainStatusBadge(state.status)}
      </div>
      <p className="text-[12.5px] text-muted-foreground mb-5">
        Verify your own domain so platform-relay emails send from your address (e.g.
        billing@yourcompany.com) and pass SPF, DKIM, and DMARC checks. Until the domain is
        verified, emails keep sending from the Worqly relay address.
      </p>

      {loading ? (
        <LoadingState variant="inline" />
      ) : !state?.domain ? (
        <form onSubmit={add} className="flex items-end gap-3">
          <div className="grid gap-1.5 flex-1 max-w-sm">
            <Label>Domain</Label>
            <Input
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder="mail.yourcompany.com"
            />
          </div>
          <Button type="submit" disabled={busy || !domainInput.trim()} className="rounded-full h-10 px-5 text-[13px] font-semibold">
            {busy ? 'Adding…' : 'Add domain'}
          </Button>
        </form>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-2 text-[14px] font-semibold text-foreground">
            <Globe className="size-4 text-muted-foreground" />
            {state.domain}
          </div>

          {state.status !== 'verified' && (
            <p className="text-[12.5px] text-muted-foreground">
              Add these records at your DNS provider (Cloudflare, GoDaddy, Route 53, …). The TXT
              records are your SPF and DKIM proof of ownership. Then hit <strong>Verify DNS</strong> —
              propagation can take minutes to a few hours.
            </p>
          )}

          {state.records.length > 0 && (
            <div className="overflow-x-auto rounded-xl ring-1 ring-border/40">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border/40">
                    <th className="px-3 py-2 font-semibold">Purpose</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                    <th className="px-3 py-2 font-semibold">Name</th>
                    <th className="px-3 py-2 font-semibold">Value</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {state.records.map((r, i) => (
                    <tr key={i} className="border-b border-border/30 last:border-0 align-top">
                      <td className="px-3 py-2 font-semibold uppercase tracking-wide text-[11px] text-foreground/80">{r.record}</td>
                      <td className="px-3 py-2 font-mono">{r.type}</td>
                      <td className="px-3 py-2 font-mono break-all">{r.name}</td>
                      <td className="px-3 py-2 font-mono break-all max-w-[22rem]">{r.value}</td>
                      <td className="px-3 py-2">
                        <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => copy(r.value)} aria-label={`Copy ${r.record} value`}>
                          <Copy className="size-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button type="button" onClick={verify} disabled={busy} className="rounded-full h-10 px-5 text-[13px] font-semibold">
              <RefreshCw className="size-3.5 mr-1.5" />
              {busy ? 'Checking…' : 'Verify DNS'}
            </Button>
            <Button type="button" variant="outline" onClick={remove} disabled={busy} className="rounded-full h-10 px-5 text-[13px] font-semibold">
              <Trash2 className="size-3.5 mr-1.5" />
              Remove domain
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function domainStatusBadge(status: SenderDomainStatus): React.ReactNode {
  if (status === 'verified') return <span className={statusPill('success')}><span className={statusDot('success')} />Verified</span>;
  if (status === 'failed')   return <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Failed</span>;
  if (status === 'pending')  return <span className={statusPill('warning')}><span className={statusDot('warning')} />Pending</span>;
  return <span className={statusPill('info')}><span className={statusDot('info')} />Not set up</span>;
}

// ─── Suppressed recipients ────────────────────────────────────────────────────

const REASON_LABEL: Record<EmailSuppression['reason'], string> = {
  bounce: 'Bounced',
  complaint: 'Marked as spam',
  manual: 'Manual',
};

function SuppressionsSection() {
  const [rows, setRows] = React.useState<EmailSuppression[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const [addEmail, setAddEmail] = React.useState('');
  const [adding, setAdding] = React.useState(false);

  React.useEffect(() => {
    emailApi.listSuppressions()
      .then(setRows)
      .catch((err: any) => toast.error('Could not load suppressions', { description: err.message }))
      .finally(() => setLoading(false));
  }, []);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const email = addEmail.trim();
    if (!email) return;
    setAdding(true);
    try {
      const suppression = await emailApi.addSuppression(email);
      setRows((rs) => {
        // Idempotent server-side — don't duplicate an already-listed address.
        if (rs.some((r) => r.id === suppression.id)) return rs;
        return [suppression, ...rs];
      });
      setAddEmail('');
      toast.success('Recipient suppressed — no further emails will be sent to this address');
    } catch (err: any) {
      toast.error('Could not suppress', { description: err.message });
    } finally {
      setAdding(false);
    }
  }

  async function remove(id: string) {
    setRemovingId(id);
    try {
      await emailApi.deleteSuppression(id);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success('Recipient un-suppressed — sends to this address resume');
    } catch (err: any) {
      toast.error('Remove failed', { description: err.message });
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
      <div className="p-6 sm:p-7 pb-0">
        <h3 className={VISUAL.dashSection}>Suppressed recipients</h3>
        <p className="text-[12.5px] text-muted-foreground mt-1 mb-4">
          Addresses that bounced or marked an email as spam are paused automatically — sends to
          them are logged as failed instead of attempted. Remove one to resume sending, or add
          one manually to stop emailing an address (e.g. on request).
        </p>
        <form onSubmit={add} className="flex items-center gap-2 mb-4">
          <Input
            type="email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            placeholder="person@example.com"
            aria-label="Email address to suppress"
            className="h-9 max-w-xs"
          />
          <Button type="submit" size="sm" variant="outline" disabled={adding || !addEmail.trim()} className="rounded-full h-9">
            {adding ? 'Adding…' : 'Add suppression'}
          </Button>
        </form>
      </div>
      <DataTable<EmailSuppression>
        loading={loading}
        rows={rows}
        rowKey={(r) => r.id}
        columns={[
          { key: 'email',  header: 'Email',  cell: (r) => <span className="font-medium">{r.email}</span> },
          {
            key: 'reason', header: 'Reason',
            cell: (r) => (
              <span className={statusPill(r.reason === 'manual' ? 'info' : 'destructive')}>
                <span className={statusDot(r.reason === 'manual' ? 'info' : 'destructive')} />
                {REASON_LABEL[r.reason]}
              </span>
            ),
          },
          { key: 'when',   header: 'Since',  cell: (r) => formatDateTime(r.created_at), className: 'text-muted-foreground' },
          {
            key: 'actions', header: '',
            cell: (r) => (
              <Button
                size="sm" variant="outline" className="rounded-full h-8"
                disabled={removingId === r.id}
                onClick={() => remove(r.id)}
              >
                {removingId === r.id ? 'Removing…' : 'Remove'}
              </Button>
            ),
          },
        ]}
        emptyState={
          <EmptyState
            icon={<MailX />}
            title="No suppressed recipients"
            description="Bounced or complaining addresses will show up here automatically."
          />
        }
      />
    </section>
  );
}
